import { ReactNode } from "react";
import {
  Box,
  Paper,
  Skeleton,
  TableBody,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TableSortLabel,
} from "@mui/material";

//Common styles
import {
  CustomTableRowTR,
  Paragraph,
  StyledTable,
  TableData,
  TableHeader,
  TableTopHeader,
  TableWrapper,
  Title,
} from "../../styles/commonStyles";

//Utils
import { formatDateTimeWithSeconds } from "../../utils/DateTimeUtils";

//Constants
const DEFAULT_SKELETON_ROWS = 5;
const DEFAULT_ROWS_PER_PAGE_OPTIONS = [10, 25, 50];

export interface StatusStyle {
  color: string;
  background: string;
}

const DEFAULT_STATUS_STYLES: Record<string, StatusStyle> = {
  OPEN: { color: "#1D4ED8", background: "rgba(37, 99, 235, 0.12)" },
  PENDING: { color: "#1D4ED8", background: "rgba(37, 99, 235, 0.12)" },
  IN_PROGRESS: { color: "#B45309", background: "rgba(245, 158, 11, 0.18)" },
  PAYMENT_IN_PROGRESS: { color: "#B45309", background: "rgba(245, 158, 11, 0.18)" },
  REFUND_IN_PROGRESS: { color: "#B45309", background: "rgba(245, 158, 11, 0.18)" },
  PARTIAL_PAID: { color: "#B45309", background: "rgba(245, 158, 11, 0.18)" },
  RESOLVED: { color: "#047857", background: "rgba(16, 185, 129, 0.16)" },
  COMPLETED: { color: "#047857", background: "rgba(16, 185, 129, 0.16)" },
  ACTIVE: { color: "#047857", background: "rgba(16, 185, 129, 0.16)" },
  PAID: { color: "#047857", background: "rgba(16, 185, 129, 0.16)" },
  CLOSED: { color: "#6B7280", background: "rgba(107, 114, 128, 0.16)" },
  REFUNDED: { color: "#6B7280", background: "rgba(107, 114, 128, 0.16)" },
  CANCELLED: { color: "#B91C1C", background: "rgba(239, 68, 68, 0.14)" },
  NOT_PAID: { color: "#B91C1C", background: "rgba(239, 68, 68, 0.14)" },
  REFUND_FAILED: { color: "#B91C1C", background: "rgba(239, 68, 68, 0.14)" },
};

const FALLBACK_STATUS_STYLE: StatusStyle = {
  color: "#6B7280",
  background: "rgba(107, 114, 128, 0.08)",
};

export interface CommonListColumn<T = any> {
  /** Column heading shown in the table header row */
  label: string;
  /** Path to the value in the row, supports nesting e.g. "cookProfile.mobile" */
  field?: string;
  /**
   * How the cell is rendered when no custom `render` is given:
   * - text (default): raw value or "-"
   * - datetime: formatted via formatDateTimeWithSeconds, kept on one line
   * - status: colored pill based on the value (OPEN / RESOLVED / ...)
   */
  type?: "text" | "datetime" | "status";
  /** Full control over the cell — receives the row, wins over field/type */
  render?: (row: T) => ReactNode;
  /** Reserve horizontal space for wide content (e.g. 180 for date-time) */
  minWidth?: number | string;
  /** Keep cell content on a single line */
  nowrap?: boolean;
  /** Set true for cells with buttons/links so clicking them does not trigger onRowClick */
  stopRowClick?: boolean;
  /** Override the status pill palette for this column (type: "status" only) */
  statusStyles?: Record<string, StatusStyle>;
  /** Show a sort arrow on this column header and make it clickable */
  sortable?: boolean;
  /** The field name sent to onSortChange — defaults to `field` */
  sortField?: string;
}

interface CommonListTableProps<T = any> {
  title?: string;
  /** Extra content on the right of the title row (e.g. a create button) */
  headerRight?: ReactNode;
  columns: CommonListColumn<T>[];
  data: T[];
  loading: boolean;
  /** Row identity; defaults to row.id */
  rowKey?: (row: T) => string | number;
  onRowClick?: (row: T) => void;
  emptyText?: string;
  skeletonRows?: number;
  /** Pagination — omit page/total to render the table without a pager */
  page?: number;
  rowsPerPage?: number;
  total?: number;
  onPageChange?: (page: number) => void;
  onRowsPerPageChange?: (rows: number) => void;
  rowsPerPageOptions?: number[];
  /**
   * When set, the table body scrolls inside the card at this height so the
   * pagination below is always visible (use on full-page list views, not tabs).
   * Example: "calc(100vh - 320px)"
   */
  maxTableHeight?: string;
  /**
   * When true the component stretches to fill its flex parent (flex: 1).
   * The table body scrolls internally; the pagination row is pinned at the
   * bottom.  Use this on full-page views instead of maxTableHeight.
   */
  fillHeight?: boolean;
  /** Current sort state passed in from the parent */
  sort?: { field: string; order: "ASC" | "DESC" };
  /** Called when the user clicks a sortable column header */
  onSortChange?: (field: string, order: "ASC" | "DESC") => void;
}

const getNestedValue = (row: any, path?: string) => {
  if (!path) return undefined;
  return path
    .split(".")
    .reduce((acc, key) => (acc == null ? undefined : acc[key]), row);
};

const renderCellContent = <T,>(row: T, column: CommonListColumn<T>) => {
  if (column.render) return column.render(row);

  const value = getNestedValue(row, column.field);

  switch (column.type) {
    case "datetime":
      return value ? formatDateTimeWithSeconds(value) : "-";
    case "status": {
      if (!value) return "-";
      const palette = column.statusStyles ?? DEFAULT_STATUS_STYLES;
      const style = palette[String(value)] ?? FALLBACK_STATUS_STYLE;
      return (
        <Paragraph
          style={{
            ...style,
            display: "inline-block",
            padding: "4px 20px",
            borderRadius: "4px",
            fontSize: "14px",
          }}
        >
          {String(value)}
        </Paragraph>
      );
    }
    default:
      return value === null || value === undefined || value === ""
        ? "-"
        : String(value);
  }
};

const CommonListTable = <T,>({
  title,
  headerRight,
  columns,
  data,
  loading,
  rowKey,
  onRowClick,
  emptyText = "No data available",
  skeletonRows = DEFAULT_SKELETON_ROWS,
  page,
  rowsPerPage = DEFAULT_ROWS_PER_PAGE_OPTIONS[0],
  total,
  onPageChange,
  onRowsPerPageChange,
  rowsPerPageOptions = DEFAULT_ROWS_PER_PAGE_OPTIONS,
  maxTableHeight,
  fillHeight,
  sort,
  onSortChange,
}: CommonListTableProps<T>) => {
  const handleSortClick = (column: CommonListColumn<T>) => {
    if (!column.sortable || !onSortChange) return;
    const field = column.sortField ?? column.field ?? column.label;
    const newOrder = sort?.field === field && sort?.order === "ASC" ? "DESC" : "ASC";
    onSortChange(field, newOrder);
  };
  const showPagination =
    !loading &&
    data.length > 0 &&
    page !== undefined &&
    total !== undefined &&
    !!onPageChange;

  const cellStyle = (column: CommonListColumn<T>) => ({
    ...(column.minWidth !== undefined && { minWidth: column.minWidth }),
    ...((column.nowrap || column.type === "datetime") && {
      whiteSpace: "nowrap" as const,
    }),
  });

  const tableContainerSx = {
    borderRadius: "8px",
    ...(fillHeight ? { flex: 1, overflowY: "auto" as const } : {}),
    ...(!fillHeight && maxTableHeight
      ? { maxHeight: maxTableHeight, overflowY: "auto" as const }
      : {}),
  };

  const tableInner = (
    <>
      {(title || headerRight) && (
        <TableTopHeader>
          <Title>{title}</Title>
          {headerRight}
        </TableTopHeader>
      )}

      <TableContainer component={Paper} sx={tableContainerSx}>
        {/* min-width on small screens makes the container scroll
            horizontally instead of crushing the columns */}
        <StyledTable
          stickyHeader={!!(fillHeight || maxTableHeight)}
          sx={{ minWidth: { xs: 640, md: "auto" } }}
        >
          <TableHead>
            <TableRow
              sx={{ backgroundColor: "rgba(232, 235, 237, 0.3) !important" }}
            >
              {columns.map((column) => {
                const sortKey = column.sortField ?? column.field;
                const isActive = !!sort && sort.field === sortKey;
                return (
                  <TableHeader
                    key={column.label}
                    style={cellStyle(column)}
                    sx={column.sortable ? { cursor: "pointer", userSelect: "none" } : undefined}
                  >
                    {column.sortable ? (
                      <TableSortLabel
                        active={isActive}
                        direction={isActive ? (sort!.order.toLowerCase() as "asc" | "desc") : "asc"}
                        onClick={() => handleSortClick(column)}
                      >
                        {column.label}
                      </TableSortLabel>
                    ) : column.label}
                  </TableHeader>
                );
              })}
            </TableRow>
          </TableHead>
          {loading &&
            Array.from({ length: skeletonRows }).map((_, index) => (
              <CustomTableRowTR key={index}>
                {columns.map((column) => (
                  <TableData key={column.label}>
                    <Skeleton height={28} />
                  </TableData>
                ))}
              </CustomTableRowTR>
            ))}
          <TableBody>
            {!loading && data.length > 0 ? (
              data.map((row: any, rowIndex) => (
                <CustomTableRowTR
                  key={rowKey ? rowKey(row) : row?.id ?? rowIndex}
                  onClick={onRowClick ? () => onRowClick(row) : undefined}
                  style={onRowClick ? { cursor: "pointer" } : undefined}
                >
                  {columns.map((column) => (
                    <TableData
                      key={column.label}
                      style={cellStyle(column)}
                      onClick={
                        column.stopRowClick
                          ? (e) => e.stopPropagation()
                          : undefined
                      }
                    >
                      {renderCellContent(row, column)}
                    </TableData>
                  ))}
                </CustomTableRowTR>
              ))
            ) : (
              !loading && (
                <TableRow>
                  <TableHeader colSpan={columns.length} align="center">
                    {emptyText}
                  </TableHeader>
                </TableRow>
              )
            )}
          </TableBody>
        </StyledTable>
      </TableContainer>
    </>
  );

  const pagination = showPagination ? (
    <TablePagination
      component="div"
      count={total as number}
      page={page as number}
      onPageChange={(_, p) => onPageChange?.(p)}
      rowsPerPage={rowsPerPage}
      onRowsPerPageChange={(e) =>
        onRowsPerPageChange?.(Number(e.target.value))
      }
      rowsPerPageOptions={rowsPerPageOptions}
      sx={{
        "& .MuiTablePagination-toolbar": { alignItems: "center" },
        "& .MuiTablePagination-selectLabel, & .MuiTablePagination-displayedRows":
          { margin: 0 },
      }}
    />
  ) : null;

  if (fillHeight) {
    return (
      <Box
        sx={{
          flex: 1,
          minHeight: 0,
          display: "flex",
          flexDirection: "column",
        }}
      >
        <TableWrapper
          style={{
            flex: 1,
            minHeight: 0,
            display: "flex",
            flexDirection: "column",
          }}
        >
          {tableInner}
        </TableWrapper>
        {pagination}
      </Box>
    );
  }

  return (
    <>
      <TableWrapper>{tableInner}</TableWrapper>
      {pagination}
    </>
  );
};

export default CommonListTable;
